/**
 * 订单详情弹窗内「订单摘要 + 菜品列表」布局块，供营业分析订单详情与风控订单详情复用（与 Figma 订单详情弹窗一致）。
 */
import React, { useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import i18n from '../../../i18n';
import {
    formatPaymentQueueAmount,
    formatPaymentQueueInvoiceFlag,
    formatPaymentQueueStatus,
    getPaymentQueueRowKey,
} from '../../../constants/payment-queue-display';
import { formatEUR } from '../../../constants/tool.fn';
import { dateFormat, formatDishMakeStatusText } from '../../../constants/tool-order';
import { CustomerLoading } from '../../../component/loading/loading';
import GlobalNotification from '../../../component/common/snackbar';
import { Dish, Order } from '../../../models';
import type { PaymentQueueItem } from '../../../models/payment-queue.entity';
import { ReactComponent as VisibilityIcon } from '../../../static/images/svg/ic_round-visibility.svg';
import { ReactComponent as VisibilityOffIcon } from '../../../static/images/svg/material-symbols_visibility-off-rounded.svg';
import {
    filterOrderDetailDishesByFreeVisibility,
    orderDetailDishListHasFreeDishes,
} from '../../../utils/order-detail-dish-free';
import { getOrderDetailDishReturnReasonDisplay } from '../../../utils/order-detail-dish-return-reason';
import { getOrderDetailDishRowKey } from '../../../utils/order-detail-dish-list';
import { resolveOrderDetailDishStatusKind } from '../../../utils/order-detail-dish-status';
import { getOrderDiningDurationMinutes } from '../../../utils/order-dining-duration';

/** 菜品表头列 flex 比例，与表头一致 */
export const ORDER_DETAIL_DISH_COL_FLEX = { info: 33, sales: 22, operation: 23, status: 22 };

/** 额外费用表三列 flex（无「菜品状态」列，总和 100 与四列表对齐习惯） */
const ORDER_DETAIL_EXTRA_DISH_COL_FLEX = { info: 38, sales: 28, operation: 34 };

/** 列间距（MUI spacing 单位） */
export const ORDER_DETAIL_COL_GAP = 3;

/** 订单行「额外费用」：`DishType.EXTRA === -1`，与普通菜品分表展示 */
export const ORDER_DETAIL_DISH_TYPE_EXTRA = -1;

export type OrderDetailSummaryLayout = 'analysis' | 'risk';

/**
 * 已结算订单：存在有效 `order_end_time` 时不展示总金额与应付金额列。
 * @param order - 订单头
 */
function isOrderSettledForSummary(order: Order): boolean {
    const end = order.order_end_time;
    if (end == null || end === undefined) {
        return false;
    }
    if (typeof end === 'string' && end.trim() === '') {
        return false;
    }
    return true;
}

/**
 * 摘要金额展示：接口未给数值（null/undefined）时留白，避免被 `?? 0` 显示成 0.00；有效数字仍走 formatEUR。
 * @param raw - 订单头上的金额字段原值
 */
function formatOrderSummaryAmountOrEmpty(raw: number | null | undefined): string {
    if (raw === null || raw === undefined) {
        return '';
    }
    const n = Number(raw);
    if (Number.isNaN(n)) {
        return '';
    }
    return formatEUR(n);
}

/**
 * 摘要区数量类字段展示：无效时留白，0 仍展示。
 * @param raw 数量原值
 */
function formatOrderSummaryCountOrEmpty(raw: number | null | undefined): string {
    if (raw === null || raw === undefined) {
        return '';
    }
    const n = Number(raw);
    if (Number.isNaN(n)) {
        return '';
    }
    return String(n);
}

export type OrderDetailOrderSummaryBlockProps = {
    /** 订单头信息，缺省时整块不渲染 */
    order: Order | null;
    /** @deprecated 兼容历史调用，摘要字段现统一按同一顺序展示 */
    layout: OrderDetailSummaryLayout;
    /** @deprecated 详情固定展示已付列，该参数已忽略 */
    hidePaidAmount?: boolean;
    /** @deprecated 详情固定展示总金额/应付/已付，该参数已忽略 */
    summaryMainAmountKind?: 'payable' | 'actual';
};

/**
 * 订单信息白底卡片：字段按统一顺序连续排列，超出容器宽度后自动换行。
 */
export function OrderDetailOrderSummaryBlock({
    order,
}: OrderDetailOrderSummaryBlockProps) {
    if (!order) return null;

    const labelTime = i18n.t('immediate_table_opening_time');
    const labelOpener = i18n.t('immediate_opener');
    /** 已结算仅展示已付金额列 */
    const isSettled = isOrderSettledForSummary(order);
    /** 开桌时间的格式化文案。 */
    const timeValue = order.order_start_time
        ? moment(order.order_start_time).format('HH:mm DD/MM/YYYY')
        : '';
    /** 结单时间的格式化文案。 */
    const endTimeValue = order.order_end_time
        ? moment(order.order_end_time).format('HH:mm DD/MM/YYYY')
        : '';
    /** 订单用餐时长，单位为分钟；未结单时留白。 */
    const diningDuration = getOrderDiningDurationMinutes(
        order.order_start_time,
        order.order_end_time,
    );
    /** 订单摘要字段标签，字段缺失时不创建空占位。 */
    const summaryLabels = [
        i18n.t('business_analysis_page_order_account'),
        i18n.t('business_analysis_page_table_name'),
        i18n.t('immediate_number_people'),
        ...(isSettled
            ? [i18n.t('immediate_paid_amount')]
            : [
                  i18n.t('immediate_order_total_amount'),
                  i18n.t('immediate_amounts_payable'),
                  i18n.t('immediate_paid_amount'),
              ]),
        labelOpener,
        labelTime,
        i18n.t('immediate_statement_time'),
        i18n.t('intelligent_risk_control_page_column_duration_minutes'),
        i18n.t('business_analysis_page_text_discount_amount_total'),
        i18n.t('business_analysis_page_text_return_dish_amount'),
        i18n.t('business_analysis_page_text_reprice_amount'),
        i18n.t('intelligent_risk_control_page_column_dish_count'),
        i18n.t('intelligent_risk_control_page_column_avg_dish_count'),
    ];
    /** 订单摘要字段值，与 {@link summaryLabels} 按下标一一对应。 */
    const summaryValues = [
        String(order.order_head_id ?? ''),
        String(order.table_name ?? ''),
        String(order.customer_num ?? ''),
        ...(isSettled
            ? [formatEUR(+(order.paid_amount ?? 0))]
            : [
                  formatOrderSummaryAmountOrEmpty(order.total_amount),
                  formatOrderSummaryAmountOrEmpty(order.should_amount),
                  formatEUR(+(order.paid_amount ?? 0)),
              ]),
        String(order.open_employee_name ?? ''),
        timeValue,
        endTimeValue,
        diningDuration == null ? '' : String(diningDuration),
        formatOrderSummaryAmountOrEmpty(order.discount_total),
        formatOrderSummaryAmountOrEmpty(order.refund_dish_total),
        formatOrderSummaryAmountOrEmpty(order.reprice_total),
        formatOrderSummaryCountOrEmpty(order.dish_count),
        formatOrderSummaryCountOrEmpty(order.avg_dish_per_person),
    ];
    /** 订单摘要字段流，统一由网格自动分配到相邻行。 */
    const summaryFields = summaryLabels.map((label, index) => ({
        label,
        value: summaryValues[index],
    }));

    return (
        <Box
            className="order-detail-dialog-order-info"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                bgcolor: 'common.white',
                borderRadius: '16px',
                boxShadow: '0px 0px 4px 0px var(--overlay-800-a25)',
                px: 2,
                py: 3,
                flexShrink: 0,
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${Math.ceil(summaryFields.length / 2)}, minmax(0, 1fr))`,
                    columnGap: ORDER_DETAIL_COL_GAP,
                    rowGap: 1,
                    textAlign: 'center',
                }}
            >
                {summaryFields.map(({ label, value }) => (
                    <Box key={label} sx={{ minWidth: 0 }}>
                        <Typography
                            component="div"
                            variant="subtitle2"
                            sx={{ color: 'var(--text-primary-300)' }}
                        >
                            {label}
                        </Typography>
                        <Typography
                            component="div"
                            variant="subtitle2"
                            sx={{ color: 'var(--text-dark)' }}
                        >
                            {value}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

/** 付款队列表列 flex，与菜品表四列布局一致 */
const PAYMENT_QUEUE_COL_FLEX = [
    ORDER_DETAIL_DISH_COL_FLEX.info,
    ORDER_DETAIL_DISH_COL_FLEX.sales,
    ORDER_DETAIL_DISH_COL_FLEX.operation,
    ORDER_DETAIL_DISH_COL_FLEX.status,
];

/** 菜品表 / 付款队列表共用白底卡片样式 */
const ORDER_DETAIL_TABLE_PANEL_SX = {
    bgcolor: 'common.white',
    borderRadius: '16px',
    boxShadow: '0px 0px 4px 0px var(--overlay-800-a25)',
    px: 2,
    py: 3,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
} as const;

/** 付款队列表头文案（四列，与 {@link OrderDetailDishTablePanel} 表头样式一致） */
const PAYMENT_QUEUE_HEADER_LABELS = [
    `${i18n.t('order_detail_payment_queue_id')}\n${i18n.t('order_detail_payment_type')}`,
    `${i18n.t('order_detail_payment_status')}\n${i18n.t('order_detail_payment_amount')}`,
    `${i18n.t('order_detail_payment_tip')}\n${i18n.t('order_detail_payment_service_fee')}`,
    i18n.t('order_detail_payment_is_invoice'),
];

/**
 * 解析单条付款队列的四列展示文案。
 * @param item 付款队列项
 */
function getPaymentQueueCellLines(item: PaymentQueueItem): string[][] {
    return [
        [item.id != null ? String(item.id) : '–', item.payment_type_name?.trim() || '–'],
        [
            formatPaymentQueueStatus(item.payment_status, i18n.t.bind(i18n)),
            formatPaymentQueueAmount(item.discounted_total_price),
        ],
        [
            formatPaymentQueueAmount(item.tip_amount),
            formatPaymentQueueAmount(item.service_amount),
        ],
        [formatPaymentQueueInvoiceFlag(item.is_create_invoice, i18n.t.bind(i18n))],
    ];
}

export type OrderDetailPaymentQueueBlockProps = {
    /** 订单头（含接口解析后的 queue） */
    order: Order | null;
};

/**
 * 付款队列表头行（整块队列共用一行表头）。
 */
function OrderDetailPaymentQueueHeaderRow() {
    return (
        <Box
            sx={{
                display: 'flex',
                gap: ORDER_DETAIL_COL_GAP,
                alignItems: 'flex-start',
                color: 'var(--text-primary-300)',
                textAlign: 'center',
                flexShrink: 0,
            }}
        >
            {PAYMENT_QUEUE_COL_FLEX.map((flexVal, i) => (
                <Box key={PAYMENT_QUEUE_HEADER_LABELS[i]} sx={{ flex: flexVal, minWidth: 0, whiteSpace: 'pre-wrap' }}>
                    {PAYMENT_QUEUE_HEADER_LABELS[i].split('\n').map((line, j) => (
                        <Typography key={j} variant="subtitle2" sx={{ mb: 0 }}>
                            {line}
                        </Typography>
                    ))}
                </Box>
            ))}
        </Box>
    );
}

/**
 * 付款队列单行数据（列宽与表头一致）。
 * @param item 付款队列项
 */
function OrderDetailPaymentQueueDataRow({ item }: { item: PaymentQueueItem }) {
    const cellLines = getPaymentQueueCellLines(item);
    return (
        <Box
            sx={{
                display: 'flex',
                gap: ORDER_DETAIL_COL_GAP,
                alignItems: 'center',
                color: 'var(--text-dark)',
                textAlign: 'center',
                minHeight: 32,
            }}
        >
            {PAYMENT_QUEUE_COL_FLEX.map((flexVal, colIndex) => (
                <Box key={colIndex} sx={{ flex: flexVal, minWidth: 0 }}>
                    {cellLines[colIndex].map((line, lineIndex) => (
                        <Typography key={lineIndex} variant="subtitle2" sx={{ mb: lineIndex === 0 ? 0 : undefined }}>
                            {line}
                        </Typography>
                    ))}
                </Box>
            ))}
        </Box>
    );
}

/**
 * 付款队列区块：一张菜品表风格白底卡片，共用表头，多行数据以横线分隔（与菜品表一致）。
 */
export function OrderDetailPaymentQueueBlock({ order }: OrderDetailPaymentQueueBlockProps) {
    const queue =
        order != null && Array.isArray(order.queue) ? order.queue : [];
    if (queue.length === 0) {
        return null;
    }

    return (
        <Box
            className="order-detail-dialog-dish-table-panel order-detail-dialog-payment-queue"
            sx={{ ...ORDER_DETAIL_TABLE_PANEL_SX, flexShrink: 0 }}
        >
            <OrderDetailPaymentQueueHeaderRow />
            {queue.map((item, queueIndex) => (
                <React.Fragment key={getPaymentQueueRowKey(item, queueIndex)}>
                    <OrderDetailPaymentQueueDataRow item={item} />
                    {queueIndex < queue.length - 1 ? orderDetailDishRowDivider() : null}
                </React.Fragment>
            ))}
        </Box>
    );
}

export type OrderDetailDishListBlockProps = {
    /** 菜品行数据 */
    list: Dish[];
    /** 表格是否加载中 */
    isTableLoading: boolean;
};

/**
 * 按状态种类取文案与颜色（单行：已退菜 → 已上菜 → 已催菜 → 制作态）。
 * @param item - 单行菜品
 */
function getDishStatusCellDisplay(item: Dish): { text: string; color: string } {
    const kind = resolveOrderDetailDishStatusKind(item);
    if (kind === 'returned') {
        return { text: i18n.t('immediate_returnable_dishes'), color: 'var(--func-error)' };
    }
    if (kind === 'served') {
        return { text: i18n.t('immediate_served'), color: 'var(--func-success)' };
    }
    if (kind === 'rushed') {
        return { text: i18n.t('immediate_has_been_urged'), color: 'var(--func-info)' };
    }
    return {
        text: formatDishMakeStatusText(item?.is_make ?? null),
        color: 'var(--text-dark)',
    };
}

/**
 * 渲染菜品状态列：仅展示一条状态；已退菜时另显退菜原因（与 App 对齐）。
 * @param item - 单行菜品
 */
function renderDishStatusCell(item: Dish) {
    const flex = ORDER_DETAIL_DISH_COL_FLEX.status;
    const { text: statusText, color: statusColor } = getDishStatusCellDisplay(item);
    /** 退菜原因文案；非退菜为 null */
    const returnReasonText = getOrderDetailDishReturnReasonDisplay(item);
    return (
        <Box sx={{ flex, minWidth: 0, textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ mb: 0, color: statusColor }}>
                {statusText}
            </Typography>
            {returnReasonText != null && (
                <Typography
                    variant="subtitle2"
                    sx={{
                        color: 'var(--func-error)',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        mt: 0.5,
                    }}
                >
                    {returnReasonText}
                </Typography>
            )}
        </Box>
    );
}

/** 行与行之间的分割线（与数据行之间一致） */
function orderDetailDishRowDivider() {
    return (
        <Box
            sx={{
                height: 0,
                borderBottom: '1px solid var(--stroke-neutral-800-a25)',
                width: '100%',
            }}
        />
    );
}

/**
 * 将订单详情菜品拆成：普通等菜品 / 额外费用（`dish_type === ORDER_DETAIL_DISH_TYPE_EXTRA`），各自保持接口返回顺序。
 */
function partitionOrderDetailDishesByType(list: Dish[]): { mainDishes: Dish[]; extraDishes: Dish[] } {
    const mainDishes: Dish[] = [];
    const extraDishes: Dish[] = [];
    for (const item of list) {
        if (item.dish_type === ORDER_DETAIL_DISH_TYPE_EXTRA) {
            extraDishes.push(item);
        } else {
            mainDishes.push(item);
        }
    }
    return { mainDishes, extraDishes };
}

/**
 * 表头文案与列 flex：普通菜品四列；额外费用三列（无菜品状态）。
 * @param includeDishStatusColumn - 是否含「菜品状态」列
 */
function getOrderDetailDishTableHeaderSpec(includeDishStatusColumn: boolean) {
    const baseLabels = [
        i18n.t('order_detail_dish_info'),
        i18n.t('order_detail_sales_data'),
        i18n.t('order_detail_operation_info'),
    ];
    const baseFlexes = includeDishStatusColumn
        ? [ORDER_DETAIL_DISH_COL_FLEX.info, ORDER_DETAIL_DISH_COL_FLEX.sales, ORDER_DETAIL_DISH_COL_FLEX.operation]
        : [
              ORDER_DETAIL_EXTRA_DISH_COL_FLEX.info,
              ORDER_DETAIL_EXTRA_DISH_COL_FLEX.sales,
              ORDER_DETAIL_EXTRA_DISH_COL_FLEX.operation,
          ];
    if (includeDishStatusColumn) {
        return {
            headerLabels: [...baseLabels, i18n.t('order_detail_dish_status')],
            dishFlexes: [...baseFlexes, ORDER_DETAIL_DISH_COL_FLEX.status],
        };
    }
    return { headerLabels: baseLabels, dishFlexes: baseFlexes };
}

type OrderDetailDishTablePanelProps = {
    /** 当前表的数据行 */
    rows: Dish[];
    /** `key` 前缀，避免两表 `order_detail_id` 撞车 */
    rowKeyPrefix: string;
    /** 是否展示「菜品状态」列（额外费用表为 false） */
    includeDishStatusColumn: boolean;
};

/**
 * 单个菜品表：独立白底卡片，列头 + 数据行（或空态），无区块标题。
 */
function OrderDetailDishTablePanel({ rows, rowKeyPrefix, includeDishStatusColumn }: OrderDetailDishTablePanelProps) {
    const { headerLabels, dishFlexes } = getOrderDetailDishTableHeaderSpec(includeDishStatusColumn);
    return (
        <Box
            className="order-detail-dialog-dish-table-panel"
            sx={{
                bgcolor: 'common.white',
                borderRadius: '16px',
                boxShadow: '0px 0px 4px 0px var(--overlay-800-a25)',
                px: 2,
                py: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: ORDER_DETAIL_COL_GAP,
                    alignItems: 'flex-start',
                    color: 'var(--text-primary-300)',
                    textAlign: 'center',
                    flexShrink: 0,
                }}
            >
                {dishFlexes.map((flexVal, i) => (
                    <Box key={i} sx={{ flex: flexVal, minWidth: 0, whiteSpace: 'pre-wrap' }}>
                        {headerLabels[i].split('\n').map((line, j) => (
                            <Typography key={j} variant="subtitle2" sx={{ mb: 0 }}>
                                {line}
                            </Typography>
                        ))}
                    </Box>
                ))}
            </Box>
            {rows.length === 0 ? (
                <Typography variant="subtitle2" sx={{ color: 'var(--text-secondary)', py: 2, textAlign: 'center' }}>
                    {i18n.t('general_text_no_data')}
                </Typography>
            ) : (
                rows.map((item, index) => (
                    <React.Fragment key={getOrderDetailDishRowKey(item, index, rowKeyPrefix)}>
                        <OrderDetailDishDataRow item={item} includeDishStatusColumn={includeDishStatusColumn} />
                        {index < rows.length - 1 && orderDetailDishRowDivider()}
                    </React.Fragment>
                ))
            )}
        </Box>
    );
}

/**
 * 单行菜品：四列（含状态）或三列（额外费用表，无状态列）；列宽与对应表头一致。
 */
function OrderDetailDishDataRow({ item, includeDishStatusColumn }: { item: Dish; includeDishStatusColumn: boolean }) {
    const fx = includeDishStatusColumn ? ORDER_DETAIL_DISH_COL_FLEX : ORDER_DETAIL_EXTRA_DISH_COL_FLEX;
    return (
        <Box
            sx={{
                display: 'flex',
                gap: ORDER_DETAIL_COL_GAP,
                alignItems: 'center',
                color: 'var(--text-dark)',
                textAlign: 'center',
                minHeight: 32,
            }}
        >
            <Box sx={{ flex: fx.info, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 0 }}>
                    {item.menu_item_id ?? ''}
                </Typography>
                <Typography variant="subtitle2">{item.menu_item_name ?? ''}</Typography>
            </Box>
            <Box sx={{ flex: fx.sales, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 0 }}>
                    {item.quantity ?? ''}
                </Typography>
                <Typography variant="subtitle2">
                    {formatEUR(+(item?.product_price ?? 0))} / {formatEUR(+(item?.actual_price ?? 0))}
                </Typography>
            </Box>
            <Box sx={{ flex: fx.operation, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 0 }}>
                    {item.order_employee_name ?? ''}
                </Typography>
                <Typography variant="subtitle2">{item.order_time ? dateFormat(String(item.order_time)) : ''}</Typography>
            </Box>
            {includeDishStatusColumn ? renderDishStatusCell(item) : null}
        </Box>
    );
}

/**
 * 订单详情菜品列表区右上角：眼睛图标切换是否展示免费菜品（默认隐藏单价为 0 的行）。
 */
function OrderDetailFreeDishVisibilityToggle({
    showFreeDishes,
    onToggle,
}: {
    /** 当前是否展示免费菜品 */
    showFreeDishes: boolean;
    /** 点击眼睛图标时切换展示状态 */
    onToggle: () => void;
}) {
    /** 无障碍与 Tooltip 文案：当前为展示态则提示「隐藏」，否则提示「显示」 */
    const labelKey = showFreeDishes ? 'order_detail_hide_free_dishes' : 'order_detail_show_free_dishes';
    const label = i18n.t(labelKey);
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                flexShrink: 0,
            }}
        >
            <Tooltip title={label}>
                <IconButton
                    aria-label={label}
                    onClick={onToggle}
                    size="small"
                    sx={{
                        color: 'var(--text-secondary)',
                        p: 0.5,
                    }}
                >
                    {showFreeDishes ? (
                        <VisibilityIcon width={20} height={20} aria-hidden />
                    ) : (
                        <VisibilityOffIcon width={20} height={20} aria-hidden />
                    )}
                </IconButton>
            </Tooltip>
        </Box>
    );
}

/**
 * 菜品信息区：额外费用表固定在上、下方为普通菜品表；均无区块标题；额外费用表无「菜品状态」列。
 */
export function OrderDetailDishListBlock({ list, isTableLoading }: OrderDetailDishListBlockProps) {
    /** 是否展示免费菜品（单价 product_price 为 0）；默认隐藏，与 CWA 分单列表一致 */
    const [showFreeDishes, setShowFreeDishes] = useState(false);
    /** 原始列表是否含免费菜，用于决定点击时切换或提示 */
    const hasFreeDishes = useMemo(() => orderDetailDishListHasFreeDishes(list), [list]);
    /** 按免费菜可见性过滤后的明细，再按类型分区 */
    const visibleList = useMemo(
        () => filterOrderDetailDishesByFreeVisibility(list, showFreeDishes),
        [list, showFreeDishes],
    );
    const { mainDishes, extraDishes } = partitionOrderDetailDishesByType(visibleList);
    const bothEmpty = !isTableLoading && mainDishes.length === 0 && extraDishes.length === 0;
    /** 免费菜切换条：仅加载中不展示，无免费菜时保留图标用于提示 */
    const freeDishToggle =
        !isTableLoading ? (
            <OrderDetailFreeDishVisibilityToggle
                showFreeDishes={showFreeDishes}
                onToggle={() => {
                    if (!hasFreeDishes) {
                        GlobalNotification.info(i18n.t('order_detail_no_free_dishes'));
                        return;
                    }
                    setShowFreeDishes((prev) => !prev);
                }}
            />
        ) : null;

    if (isTableLoading) {
        return (
            <Box
                className="order-detail-dialog-dish-table-panel"
                sx={{
                    bgcolor: 'common.white',
                    borderRadius: '16px',
                    boxShadow: '0px 0px 4px 0px var(--overlay-800-a25)',
                    px: 2,
                    py: 3,
                    position: 'relative',
                    flex: 1,
                    minHeight: 120,
                }}
            >
                <CustomerLoading />
            </Box>
        );
    }

    if (bothEmpty) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    flex: 1,
                    minHeight: 0,
                }}
            >
                {freeDishToggle}
                <Box
                    className="order-detail-dialog-dish-table-panel"
                    sx={{
                        bgcolor: 'common.white',
                        borderRadius: '16px',
                        boxShadow: '0px 0px 4px 0px var(--overlay-800-a25)',
                        px: 2,
                        py: 3,
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ color: 'var(--text-secondary)', py: 2, textAlign: 'center' }}>
                        {i18n.t('general_text_no_data')}
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                flex: 1,
                minHeight: 0,
            }}
        >
            {freeDishToggle}
            <Box
                className="order-detail-dialog-dish-stack"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    overflow: 'auto',
                    flex: 1,
                    minHeight: 0,
                }}
            >
                {extraDishes.length > 0 && (
                    <OrderDetailDishTablePanel rows={extraDishes} rowKeyPrefix="extra" includeDishStatusColumn={false} />
                )}
                <OrderDetailDishTablePanel rows={mainDishes} rowKeyPrefix="main" includeDishStatusColumn />
            </Box>
        </Box>
    );
}
